import React, { useState, useEffect, useCallback } from 'react';
import { Box, Paper, Typography, Button, Stack, Alert, List, ListItemButton, ListItemAvatar, ListItemText, Avatar, CircularProgress } from '@mui/material';
import { LockOutlined as LockIcon, Business as BusinessIcon, MailOutline as InvitationIcon } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'shared/contexts/AuthContext';
import { companyApi } from 'shared/services/companyApi';
import { CompanyRelationship } from 'shared/services/companyRelationshipApi';
import { ReviewInvitationDialog } from 'shared/components/ReviewInvitationDialog';
import { queryKeys } from 'shared/hooks/queryConfig';

interface Props {
  companyName?: string;
}

/**
 * Landing screen for a provider's own (non-external) staff while their
 * session's active company is their own provider company and it has no
 * active plan. Login/switch-company deliberately don't filter this
 * company out of the user's companies[] list (see
 * getOwnPlanlessProviderBlock on the BE) — they're let in specifically so
 * they land here instead of the normal app, and can still switch into any
 * client company they're externally assigned to.
 */
export const ProviderPlanBlockedScreen: React.FC<Props> = ({ companyName }) => {
  const { user, logout, switchCompany } = useAuth();
  const queryClient = useQueryClient();
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Client companies this person is externally assigned to (lent in via a
  // provider relationship) — the only other place they can go from here.
  const clientEntries = ((user?.companies || []) as any[])
    .filter((c) => c?.company && typeof c.company === 'object' && c.isActive !== false && c.isExternal)
    .map((c) => ({ id: String(c.company._id || c.company.id), name: c.company.name as string }));

  // The own (non-external) membership — this provider company itself. Same
  // criterion the backend's getOwnPlanlessProviderBlock uses to decide
  // whether to block in the first place.
  const ownCompanyEntry = ((user?.companies || []) as any[]).find(
    (c) => c?.company && typeof c.company === 'object' && c.isActive !== false && !c.isExternal,
  );
  const providerCompanyId = ownCompanyEntry
    ? String(ownCompanyEntry.company._id || ownCompanyEntry.company.id)
    : null;

  // A brand-new plan-less provider has no way to reach "Mis Clientes" to
  // accept their very first invitation — that screen is itself blocked by
  // this same gate, a real deadlock. Surface any pending invitation right
  // here instead, reusing the same ReviewInvitationDialog platform staff
  // already use as their workaround for this.
  const [pending, setPending] = useState<CompanyRelationship[]>([]);
  const [loadingPending, setLoadingPending] = useState(true);
  const [reviewing, setReviewing] = useState<CompanyRelationship | null>(null);

  const loadPending = useCallback(async () => {
    if (!providerCompanyId) {
      setLoadingPending(false);
      return;
    }
    try {
      const result = await companyApi.getClients(providerCompanyId, { limit: 100 });
      const rows = (result?.data || []) as unknown as CompanyRelationship[];
      setPending(rows.filter((r: any) => r.status === 'esperando_proveedor'));
    } catch {
      // Non-critical — the rest of the screen still works without this.
    } finally {
      setLoadingPending(false);
    }
  }, [providerCompanyId]);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  const handleInvitationResolved = () => {
    setReviewing(null);
    loadPending();
    // Accepting doesn't grant a plan by itself, so this screen will likely
    // still render afterward — but now with the client entry populated, so
    // the "switch to your assigned clients" list below picks it up.
    queryClient.invalidateQueries({ queryKey: queryKeys.companyRelationships.myProviderPlanBlock() });
  };

  const handleSwitch = async (companyId: string) => {
    if (switchingId) return;
    setSwitchingId(companyId);
    setError(null);
    try {
      await switchCompany(companyId);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'No se pudo cambiar de empresa');
      setSwitchingId(null);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: { xs: 2, md: 4 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 700,
          p: { xs: 2, md: 3 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          mt: 2,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
          <LockIcon color="warning" sx={{ mt: 0.25 }} />
          <Box>
            <Typography variant="h6" component="h1">
              {companyName || 'Su empresa'} no tiene un plan activo
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Mientras su empresa proveedora no tenga un plan propio activo, no puede
              gestionar su propia empresa en la plataforma. Contacte a su Soporte Monitor
              para adquirir un plan.
            </Typography>
          </Box>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {pending.length > 0 && (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {pending.length === 1
                ? 'Tiene una invitación pendiente de una empresa cliente — revísela para continuar.'
                : `Tiene ${pending.length} invitaciones pendientes de empresas cliente — revíselas para continuar.`}
            </Alert>
            <List sx={{ mb: 1 }}>
              {pending.map((r: any) => (
                <ListItemButton
                  key={r._id}
                  onClick={() => setReviewing(r)}
                  sx={{ borderRadius: 1.5, border: '1px solid', borderColor: 'divider', mb: 1 }}
                >
                  <ListItemAvatar>
                    <Avatar variant="rounded" sx={{ bgcolor: 'warning.light', color: 'warning.dark' }}>
                      <InvitationIcon fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={r.clientCompany?.name || 'Empresa cliente'} secondary="Revisar invitación" />
                </ListItemButton>
              ))}
            </List>
          </>
        )}

        {clientEntries.length > 0 ? (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Puede seguir trabajando en las empresas cliente a las que está asignado:
            </Alert>
            <List sx={{ mb: 1 }}>
              {clientEntries.map((c) => (
                <ListItemButton
                  key={c.id}
                  onClick={() => handleSwitch(c.id)}
                  disabled={!!switchingId}
                  sx={{ borderRadius: 1.5, border: '1px solid', borderColor: 'divider', mb: 1 }}
                >
                  <ListItemAvatar>
                    <Avatar variant="rounded" sx={{ bgcolor: 'action.hover', color: 'text.secondary' }}>
                      <BusinessIcon fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={c.name} />
                  {switchingId === c.id && <CircularProgress size={18} />}
                </ListItemButton>
              ))}
            </List>
          </>
        ) : (
          !loadingPending && pending.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Todavía no está asignado a ninguna empresa cliente.
            </Alert>
          )
        )}

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={logout}>
            Cerrar sesión
          </Button>
        </Box>
      </Paper>

      {providerCompanyId && (
        <ReviewInvitationDialog
          open={!!reviewing}
          onClose={() => setReviewing(null)}
          invitation={reviewing}
          providerCompanyId={providerCompanyId}
          onSuccess={handleInvitationResolved}
        />
      )}
    </Box>
  );
};

export default ProviderPlanBlockedScreen;
